import * as React from 'react';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import NumberField, { INumberChangedEvent } from '../NumberField/NumberField';
import CurrencyField from '../Currency/CurrencyField';
import { CreditRequestProductLineModel } from '../../models/credit-request';

interface ICreditRequestProductLinesProps {
    productLines: CreditRequestProductLineModel[];
    disabled: boolean;
    getReturnQuantity(productLine: CreditRequestProductLineModel): number;
    getReturnQuantityError(productLine: CreditRequestProductLineModel): string | null;
    getRestockingFeeApplied(productLine: CreditRequestProductLineModel): boolean;
    getRestockingFeePercentage(productLine: CreditRequestProductLineModel): number;
    getRestockingFeeAmount(productLine: CreditRequestProductLineModel): number;
    getReturnValue(productLine: CreditRequestProductLineModel): number;
    getRefundAmount(productLine: CreditRequestProductLineModel): number;
    onRestockingFeeAppliedChange(productLine: CreditRequestProductLineModel, checked: boolean): void;
    onRestockingFeePercentageChange(productLine: CreditRequestProductLineModel, percentage: number): void;
    onReturnQuantityChange(productLine: CreditRequestProductLineModel, quantity: number): void;
}

interface IProductRowProps extends ICreditRequestProductLinesProps {
    productLine: CreditRequestProductLineModel;
}

const formatQuantity = (quantity: number): string => {
    if (Number.isInteger(quantity)) return quantity.toString();
    return quantity.toFixed(3).replace(/\.?0+$/, '');
};

const formatAvailableUnits = (productLine: CreditRequestProductLineModel): string => {
    return `${formatQuantity(productLine.availableQuantity)} ${productLine.purchaseUnit || 'Units'}`;
};

const ProductRow = observer((props: IProductRowProps) => {
    const { productLine } = props;
    const restockingFeeApplied = props.getRestockingFeeApplied(productLine);

    return (
        <React.Fragment>
            <Grid item sm={1}>
                <p>{productLine.code}</p>
            </Grid>
            <Grid item sm={3}>
                <p>{productLine.description}</p>
            </Grid>
            <Grid item sm={1}>
                <p>{formatAvailableUnits(productLine)}</p>
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`pricePerUnit-${productLine.sourceOrderProductItemId}`}
                    value={productLine.unitPrice}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
            <Grid item sm={1} className="credit-product-number-input credit-product-entry">
                <NumberField
                    value={props.getReturnQuantity(productLine)}
                    error={props.getReturnQuantityError(productLine)}
                    onChange={(e: INumberChangedEvent) =>
                        props.onReturnQuantityChange(productLine, e.parsedValue)
                    }
                    formatProps={{ decimalScale: 3, allowNegative: false }}
                    textProps={{ disabled: props.disabled }}
                />
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`returnValue-${productLine.sourceOrderProductItemId}`}
                    value={props.getReturnValue(productLine)}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
            <Grid item sm={1} className="credit-product-checkbox">
                <Checkbox
                    checked={restockingFeeApplied}
                    disabled={props.disabled}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                        props.onRestockingFeeAppliedChange(productLine, checked)
                    }
                />
            </Grid>
            <Grid item sm={1} className="credit-product-number-input credit-product-entry">
                {restockingFeeApplied && (
                    <NumberField
                        value={props.getRestockingFeePercentage(productLine)}
                        error={productLine.errors.restockingFeePercentage}
                        onChange={(e: INumberChangedEvent) =>
                            props.onRestockingFeePercentageChange(productLine, e.parsedValue)
                        }
                        formatProps={{ decimalScale: 2, allowNegative: false }}
                        textProps={{ disabled: props.disabled }}
                    />
                )}
            </Grid>
            <Grid item sm={1}>
                {restockingFeeApplied && (
                    <CurrencyField
                        name={`restockingFee-${productLine.sourceOrderProductItemId}`}
                        value={props.getRestockingFeeAmount(productLine)}
                        disabled
                        onChange={() => undefined}
                        fullWidth={false}
                    />
                )}
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`refundAmount-${productLine.sourceOrderProductItemId}`}
                    value={props.getRefundAmount(productLine)}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
        </React.Fragment>
    );
});

@observer
export class CreditRequestProductLines extends React.Component<ICreditRequestProductLinesProps> {
    public render() {
        return (
            <Grid container className="section">
                <Grid item sm={1} />
                <Grid item sm={10}>
                    <h2>Products to Return</h2>
                    <Grid container className="credit-product-header">
                        <Grid item sm={1}><p><strong>Code</strong></p></Grid>
                        <Grid item sm={3}><p><strong>Name</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Available Units</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Unit Price (ex GST)</strong></p>
                        </Grid>
                        <Grid item sm={1}><p><strong>Return Qty</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Return Value (ex GST)</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Restocking Fee Applied</strong></p>
                        </Grid>
                        <Grid item sm={1}><p><strong>Fee %</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Restocking Fee (ex GST)</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Total Refund amount (ex GST)</strong></p>
                        </Grid>
                    </Grid>
                    {this.props.productLines.map(line => (
                        <Grid
                            container
                            alignItems="center"
                            className="credit-product-row"
                            key={line.sourceOrderProductItemId}
                        >
                            <ProductRow
                                {...this.props}
                                productLine={line}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        );
    }
}

export default CreditRequestProductLines;
